import {
  DirectoryGroupingConsts, ItemTypeConsts, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { cloneDeep, isEqual, trim } from 'lodash';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import GroupsConstants from 'client/util/Constants/GroupsConstants';
import ModelMixin from 'client/store/modules/models/mixins/ModelMixin';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Util from 'client/util/Util';
import createModel from 'client/store/modules/models/createModel';

import * as DynamicGroupsReusable from 'client/store/modules/models/reusables/DynamicGroupsReusable';
import { featureFlagToProviderMap, featureFlags } from '../FeatureFlagsModule';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;
const { attributesType, reservedGroupAttributes } = GroupsConstants;
const sambaModel = StoreModelMap[ItemTypeConsts.samba];

/**
 * @todo Refactor the way of handling the "userAttributes" property as an object.
 * Use a single array instead of using an object with multiple arrays to handle the four literals.
 */
export const getDefaultItem = () => ({
  activeDirectories: [],
  attributes: null,
  description: '',
  email: '',
  hasPosixGroup: false,
  id: '',
  isSambaAuthenticationEnabled: false,
  membershipAutomated: false,
  membershipUpdates: true,
  name: '',
  passwordLessSudoEnabled: false,
  posixGroupId: '',
  posixGroupName: '',
  radiusReplyErrors: [],
  sambaEnabled: false,
  sudoEnabled: false,
  type: ItemTypeConsts.userGroup,
  totalDeviceGroupCount: 0,
  userAttributes: [],
  ...DynamicGroupsReusable.getDefaultDynamicGroupItem(),
});

const model = createModel(getDefaultItem, 'id', UserGroupApi);

export default {
  namespaced: true,

  state: {
    ...model.state,
    ...DynamicGroupsReusable.getDefaultDynamicGroupState(),
  },

  actions: {
    ...model.actions,

    setIsPendingReview: DynamicGroupsReusable.setIsPendingReview,

    setMemberQuery: DynamicGroupsReusable.setMemberQuery,
    setMemberQueryUI: DynamicGroupsReusable.setMemberQueryUI,

    updateMembershipMethod: DynamicGroupsReusable.updateMembershipMethod,
    updateMemberSuggestionsNotify: DynamicGroupsReusable.updateMemberSuggestionsNotify,

    addFilter: DynamicGroupsReusable.addFilter,
    updateFilterAttribute: DynamicGroupsReusable.updateFilterAttribute,
    updateFilterOperator: DynamicGroupsReusable.updateFilterOperator,

    addFilterValue: DynamicGroupsReusable.addFilterValue,
    updateFilterValue: DynamicGroupsReusable.updateFilterValue,
    removeFilterValue: DynamicGroupsReusable.removeFilterValue,

    addMemberQueryExemption: DynamicGroupsReusable.addMemberQueryExemption,
    removeMemberQueryExemption: DynamicGroupsReusable.removeMemberQueryExemption,
    updateMemberQueryExemptions: DynamicGroupsReusable.updateMemberQueryExemptions,
    updateExemptionOperation: DynamicGroupsReusable.updateExemptionOperation,
    mapMembershipExemptions: DynamicGroupsReusable.mapMembershipExemptions,
    syncExemptionWithItemSelection: DynamicGroupsReusable.syncExemptionWithItemSelection,

    resetIdIncrement: DynamicGroupsReusable.resetIdIncrement,

    resetMemberQueryUI: DynamicGroupsReusable.resetMemberQueryUI,

    resetMembershipData: DynamicGroupsReusable.resetMembershipData,

    setPreviousItemMemberQueryUI: DynamicGroupsReusable.setPreviousItemMemberQueryUI,

    addAttribute({ commit, state }, { attributeType, name, value }) {
      const item = cloneDeep(state.item);
      const tempId = Util.getUniqueId();
      const config = {
        tempId,
        name: name || '',
        value: value || '',
      };

      switch (attributeType) {
        case attributesType.boolean:
          item.userAttributes.push({
            ...config,
            type: attributesType.boolean,
            value: value === undefined || value,
          });
          break;
        case attributesType.number:
          item.userAttributes.push({
            ...config,
            type: attributesType.number,
          });
          break;
        case attributesType.string:
          item.userAttributes.push({
            ...config,
            type: attributesType.string,
          });
          break;
        case attributesType.object:
          item.userAttributes.push({
            ...config,
            type: attributesType.object,
            value: JSON.stringify(value === null ? {} : value, undefined, 2),
          });
          break;
        default:
          break;
      }

      commit('SET_ITEM', { userAttributes: item.userAttributes });
    },

    bindSudoStatus(context) {
      const { id } = context.getters;
      const targetsToUpdate = context.getters.getSudoersToUpdate;

      const payload = {
        parentType: groupTypes.userGroup,
        parentId: id,
        targetType: groupTypes.systemGroup,
        targetsToUpdate,
      };

      return context.dispatch(
        'DirectoryGroupingModule/bindAttributes',
        payload,
        { root: true },
      );
    },

    async fetchItem(context) {
      await model.actions.fetchItem(context);

      const {
        commit,
        dispatch,
        getters,
        rootState,
        state,
      } = context;
      const { id } = getters;
      const parentType = groupTypes.userGroup;

      let activeDirectories;
      let googleWorkspaceDirectories = null;
      let managementGroupsEnabled = false;

      if (id) {
        activeDirectories = await UserGroupApi.fetchActiveDirectories(id);
        googleWorkspaceDirectories = await DirectoryGroupingApi
          .getAssociations(parentType, id, ItemTypeConsts.gSuite);
        if (googleWorkspaceDirectories
            && googleWorkspaceDirectories.length) {
          const gSuiteId = googleWorkspaceDirectories[0].to.id;
          await GSuiteApi.get(gSuiteId).then((res) => {
            const { groupsEnabled } = res;
            managementGroupsEnabled = groupsEnabled;
          });
        }
      }

      const fillIdsToAttributes = attributeList => attributeList.map(attribute => (
        { tempId: Util.getUniqueId(), name: attribute.name, value: attribute.value }
      ));

      let { attributes } = state.item;
      const tempRadiusAttributes = attributes
      && attributes.radius
      && attributes.radius.reply.length > 0
        ? fillIdsToAttributes(attributes.radius.reply) : [];

      attributes = attributes && attributes.radius
        ? { ...attributes, radius: { reply: [...tempRadiusAttributes] } } : attributes;

      const posixGroup = attributes && attributes.posixGroups ? attributes.posixGroups[0] : null;
      const hasPosixGroup = !!posixGroup;
      const posixGroupId = (posixGroup && posixGroup.id) || '';
      const posixGroupName = (posixGroup && posixGroup.name) || '';
      const sambaEnabled = (attributes && attributes.sambaEnabled) || false;
      const sudoEnabled = (attributes && attributes.sudo && attributes.sudo.enabled) || false;
      const passwordLessSudoEnabled = (attributes && attributes.sudo
        && attributes.sudo.withoutPassword) || false;
      const item = {
        activeDirectories,
        attributes,
        googleWorkspaceDirectories,
        hasPosixGroup,
        managementGroupsEnabled,
        passwordLessSudoEnabled,
        posixGroupId,
        posixGroupName,
        sambaEnabled,
        sudoEnabled,
      };

      commit('SET_ITEM', cloneDeep(item));
      commit('SET_PREVIOUS_ITEM', cloneDeep(item));

      const enableDynamicUserGroupsUI = rootState.FeatureFlagsModule
        .betaFeatures[featureFlags.dynamicUserGroupsUI];

      if (enableDynamicUserGroupsUI && getters.isItemBeingUpdated) {
        await dispatch('mapMembershipExemptions', ItemTypeConsts.userGroup);
      }
    },

    async fetchUserGroupItem(context, { id }) {
      const userGroup = id ? await UserGroupApi.get(id) : {};
      return userGroup;
    },

    async fetchDeviceGroupItems(context, { id }) {
      const {
        results,
        totalDeviceGroupCount,
      } = await UserGroupApi.fetchDeviceGroups(id);
      context.commit('SET_ITEM', { id, totalDeviceGroupCount });
      return results;
    },

    bindUserGroupToUsers({ dispatch }) {
      return dispatch('bindAssociations', {
        parentType: groupTypes.userGroup,
        targetType: memberTypes.user,
      });
    },

    bindUserGroupToSystemGroups({ dispatch }) {
      return dispatch('bindAssociations', {
        parentType: groupTypes.userGroup,
        targetType: groupTypes.systemGroup,
      });
    },

    bindUserGroupToApplications({ dispatch }, targetType = ItemTypeConsts.application) {
      return dispatch('bindAssociations', {
        parentType: groupTypes.userGroup,
        targetType,
      });
    },

    bindUserGroupToRadiusServers({ dispatch }) {
      return dispatch('bindAssociations', {
        parentType: groupTypes.userGroup,
        targetType: ItemTypeConsts.radiusServer,
      });
    },

    bindUserGroupToDirectories({ dispatch }) {
      return dispatch('confirmAndBindAssociations', {
        parentType: groupTypes.userGroup,
        targetType: ItemTypeConsts.directory,
      });
    },

    async checkIsSambaAuthenticationEnabled({ commit, dispatch, rootState }, { ldapServerId }) {
      await dispatch(`${sambaModel}/fetchItem`, { ldapServerId }, { root: true });
      const isSambaAuthenticationEnabled = rootState[sambaModel].item.authenticationEnabled;

      commit('SET_ITEM', { isSambaAuthenticationEnabled });
      commit('SET_PREVIOUS_ITEM', { isSambaAuthenticationEnabled });
    },

    async handleGroupBindings({ dispatch }, {
      applicationTargetType = ItemTypeConsts.application,
    } = {}) {
      await Promise.all([
        dispatch('bindUserGroupToUsers'),
        dispatch('bindUserGroupToSystemGroups'),
        dispatch('bindUserGroupToApplications', applicationTargetType),
        dispatch('bindUserGroupToRadiusServers'),
        dispatch('bindUserGroupToDirectories'),
        dispatch('bindSudoStatus'),
      ]);
    },

    removeAttribute(context, { tempId }) {
      const {
        commit,
        state,
      } = context;

      const userAttributes = cloneDeep(state.item.userAttributes);
      const index = userAttributes.findIndex(userAttribute => userAttribute.tempId === tempId);

      userAttributes.splice(index, 1);

      commit('SET_ITEM', { userAttributes });
    },

    resetItem(context) {
      model.actions.resetItem(context);
      context.dispatch(`${sambaModel}/resetItem`, null, { root: true });
    },

    /**
     * Override action from factory function to construct a user group object to pass to the
     * /api/v2/usergroups endpoint from the User Group representation found in the state.
     */
    async saveItem({
      commit,
      dispatch,
      getters,
      state,
      rootState,
    }) {
      const { betaFeatures } = rootState.FeatureFlagsModule;
      // eslint-disable-next-line max-len
      const enableDynamicUserGroupsUI = betaFeatures[featureFlagToProviderMap[featureFlags.dynamicUserGroupsUI]];

      const item = cloneDeep(state.item);
      const convertedUserAttributes = getters.getConvertedUserAttributes;

      // Add posixGroups attribute. Note that once a Posix group is created for User Group it
      // cannot be removed, so that case isn't handled here!
      if (item.hasPosixGroup) {
        if (!item.attributes) {
          item.attributes = {};
        }

        if (!item.attributes.posixGroups) {
          item.attributes.posixGroups = [{}];
        }

        item.attributes.posixGroups[0].id = Number(item.posixGroupId);
        item.attributes.posixGroups[0].name = item.posixGroupName;
      }

      // Add/remove the Samba Enabled attribute from the User Group
      if (!item.sambaEnabled && state.previousItem.sambaEnabled) {
        delete item.attributes.sambaEnabled;
      } else if (item.sambaEnabled && !state.previousItem.sambaEnabled) {
        if (!item.attributes) {
          item.attributes = {};
        }

        item.attributes.sambaEnabled = true;
      }

      // Add/remove sudo Enabled attribute from the User Group
      if (!item.sudoEnabled && state.previousItem.sudoEnabled) {
        delete item.attributes.sudo;
      } else if (item.sudoEnabled && !state.previousItem.sudoEnabled) {
        if (!item.attributes) {
          item.attributes = {};
        }

        item.attributes.sudo = {
          enabled: item.sudoEnabled,
          withoutPassword: item.passwordLessSudoEnabled,
        };
      } else if (item.sudoEnabled && state.previousItem.sudoEnabled) {
        if (!item.attributes) {
          item.attributes = {};
        }

        item.attributes.sudo = {
          enabled: item.sudoEnabled,
          withoutPassword: item.passwordLessSudoEnabled,
        };
      }

      // update name in ldapGroups attribute
      if (item.attributes && item.attributes.ldapGroups) {
        item.attributes.ldapGroups[0].name = item.name;
      }

      if (item.attributes) {
        reservedGroupAttributes.forEach((attribute) => {
          if (item.attributes[attribute]) {
            convertedUserAttributes[attribute] = item.attributes[attribute];
          }
        });
      }

      if (Object.keys(convertedUserAttributes).length !== 0) {
        item.attributes = convertedUserAttributes;
      }

      // Remove attributes not supported by API
      delete item.activeDirectories;
      delete item.hasPosixGroup;
      delete item.isSambaAuthenticationEnabled;
      delete item.membershipUpdates;
      delete item.passwordLessSudoEnabled;
      delete item.posixGroupId;
      delete item.posixGroupName;
      delete item.sambaEnabled;
      delete item.sudoEnabled;
      delete item.userAttributes;

      let exemptions;

      if (enableDynamicUserGroupsUI) {
        delete item.membershipAutomated;

        await dispatch('setMemberQuery');
        item.memberQuery = getters.item.memberQuery;

        exemptions = getters.getMembershipExemptions;
      } else {
        /**
         * HY-2390 removing membershipMethod for now. When getting the UG GET back,
         * we automatically bind `membershipMethod` to state when SI is not equipped to
         * receive that yet.
         */
        delete item.membershipMethod;
      }

      delete item.memberQueryUI;

      let savedItem;

      if (getters.isItemBeingCreated) {
        savedItem = await UserGroupApi.create(item);
      } else {
        savedItem = await UserGroupApi.update(item);
      }

      // Commit the saved group, set item state to update
      commit('SET_ITEM', cloneDeep(savedItem));
      commit('SET_PREVIOUS_ITEM', cloneDeep(savedItem));
      commit('SET_ITEM_STATE', { update: true });

      if (enableDynamicUserGroupsUI) {
        commit('UPDATE_MEMBER_QUERY_EXEMPTIONS', { newValue: exemptions });
      }

      return state.item;
    },

    setItemField(context, { key, value }) {
      if (key === 'memberQuery') {
        /**
         * Disable Automation and Email notifications
         * if there are any invalid filters or no filters at all.
         * @see https://jumpcloud.atlassian.net/browse/HY-2247
         */
        const shouldOverwrite = !value.filters?.length || value.filters?.every(filter => filter.field === '');

        if (shouldOverwrite) {
          // Disable Automation
          model.actions.setItemField(context, { key: 'membershipAutomated', value: false });

          // Disable Email notifications
          model.actions.setItemField(context, { key: 'memberSuggestionsNotify', value: false });
        }
      }

      context.commit('SET_ITEM', { [key]: value });
    },

    updateAttribute({ commit, state }, {
      tempId,
      value,
      key,
    }) {
      const userAttributes = cloneDeep(state.item.userAttributes);
      const index = userAttributes.findIndex(userAttribute => userAttribute.tempId === tempId);
      userAttributes[index][key] = value;

      commit('SET_ITEM', { userAttributes });
    },

    addNewRadiusReplyAttributeRow({ commit, state }) {
      let attributes = cloneDeep(state.item.attributes);
      const radiusReplyAttributes = attributes && attributes.radius ? attributes.radius.reply : [];
      attributes = { ...attributes, radius: { reply: [...radiusReplyAttributes, { tempId: Util.getUniqueId(), name: '', value: '' }] } };
      commit('SET_ITEM', { attributes });
    },

    updateRadiusReplyAttribute({ commit, state }, { index, listItem }) {
      let attributes = cloneDeep(state.item.attributes);
      const radiusReplyAttributes = cloneDeep(state.item.attributes.radius.reply);
      const trimmedListItem = {
        tempId: listItem.tempId,
        name: trim(listItem.name),
        value: trim(listItem.value),
      };

      radiusReplyAttributes[index] = { ...radiusReplyAttributes[index], ...trimmedListItem };

      attributes = { ...attributes, radius: { reply: [...radiusReplyAttributes] } };

      commit('SET_ITEM', { attributes });
    },

    removeRadiusReplyAttribute({ commit, state }, { index }) {
      let attributes = cloneDeep(state.item.attributes);
      const radiusReplyAttributes = cloneDeep(state.item.attributes.radius.reply);
      radiusReplyAttributes.splice(index, 1);

      attributes = { ...attributes, radius: { reply: [...radiusReplyAttributes] } };

      commit('SET_ITEM', { attributes });
    },

    updateRadiusReplyErrors({ commit }, { radiusReplyErrors }) {
      commit('SET_ITEM', { radiusReplyErrors });
    },
  },

  getters: {
    ...model.getters,
    ...ModelMixin.getters,

    getIsPendingReview: DynamicGroupsReusable.getIsPendingReview,
    getHasMembershipChanged: DynamicGroupsReusable.getHasMembershipChanged,
    getHasMembershipMethodChanged: DynamicGroupsReusable.getHasMembershipMethodChanged,
    getHaveMembershipFiltersChanged: DynamicGroupsReusable.getHaveMembershipFiltersChanged,
    getMemberSuggestionsNotify: DynamicGroupsReusable.getMemberSuggestionsNotify,
    getMembershipMethod: DynamicGroupsReusable.getMembershipMethod,
    getMembershipFilters: DynamicGroupsReusable.getMembershipFilters,
    getFilterByIndex: DynamicGroupsReusable.getFilterByIndex,
    getMemberQueryUI: DynamicGroupsReusable.getMemberQueryUI,
    getMembershipExemptions: DynamicGroupsReusable.getMembershipExemptions,
    getPreviousMembershipExemptions: DynamicGroupsReusable.getPreviousMembershipExemptions,
    getMembershipExemptionsIds: DynamicGroupsReusable.getMembershipExemptionsIds,
    getMembershipExclusions: DynamicGroupsReusable.getMembershipExclusions,
    getMembershipInclusions: DynamicGroupsReusable.getMembershipInclusions,
    getHaveExemptionsChanged: DynamicGroupsReusable.getHaveExemptionsChanged,

    displayName(state) {
      return state.item.name;
    },

    getBooleanUserAttributes(state) {
      return state.item.userAttributes
        .filter(userAttribute => userAttribute.type === attributesType.boolean);
    },

    getNumberUserAttributes(state) {
      return state.item.userAttributes
        .filter(userAttribute => userAttribute.type === attributesType.number);
    },

    getObjectUserAttributes(state) {
      return state.item.userAttributes
        .filter(userAttribute => userAttribute.type === attributesType.object);
    },

    getSudoersToUpdate(state, getters, rootState, rootGetters) {
      const module = StoreModuleMap[ItemTypeConsts.systemGroup];
      return rootGetters[`${module}/getSudoersToUpdate`];
    },

    getStringUserAttributes(state) {
      return state.item.userAttributes
        .filter(userAttribute => userAttribute.type === attributesType.string);
    },

    getUserAttributes(state) {
      return state.item.userAttributes;
    },

    getConvertedUserAttributes(state, getters) {
      return getters.getUserAttributes
        .reduce((acc, { name, type, value }) => {
          let attributeValue;
          switch (type) {
            case attributesType.boolean:
              attributeValue = Boolean(value);
              break;
            case attributesType.number:
              attributeValue = Number(value);
              break;
            case attributesType.object:
              attributeValue = JSON.parse(value);
              break;
            case attributesType.string:
              attributeValue = String(value);
              break;
            default:
              attributeValue = '';
              break;
          }
          return {
            [name]: attributeValue,
            ...acc,
          };
        }, {});
    },

    getRadiusReplyAttributes(state) {
      return state.item.attributes && state.item.attributes.radius
        ? state.item.attributes.radius.reply : [];
    },

    getRadiusReplyErrors(state) {
      return state.item && state.item.radiusReplyErrors
        ? state.item.radiusReplyErrors : [];
    },

    getTotalDeviceGroupCount(state) {
      return state.item.totalDeviceGroupCount;
    },

    haveAssociationsChanged(state, getters) {
      const types = [
        ItemTypeConsts.systemUser,
        ItemTypeConsts.systemGroup,
        ItemTypeConsts.application,
        ItemTypeConsts.radiusServer,
        ItemTypeConsts.directory,
      ];

      return model.getters.haveAssociationsChanged(state, getters)(types);
    },

    // override hasItemChanged from default createModel.js
    hasItemChanged(state) {
      const item = cloneDeep(state.item);
      const previousItem = cloneDeep(state.previousItem);
      // we need to remove this in order to get an accurate comparison
      delete item.memberQueryUI;
      delete previousItem.memberQueryUI;
      return !isEqual(item, previousItem);
    },

    getSystemGroupAssociationCounts(state, getters) {
      const systemGroupsToBind = getters.getTargetsToBind(groupTypes.systemGroup);
      const systemGroupsToUnbind = getters.getTargetsToUnbind(groupTypes.systemGroup);
      return { bind: systemGroupsToBind.length, unbind: systemGroupsToUnbind.length };
    },

    isManagedByActiveDirectory(state) {
      const { activeDirectories } = state.item;
      return Boolean(activeDirectories && activeDirectories.length);
    },
  },

  mutations: {
    ...model.mutations,

    SET_IS_PENDING_REVIEW: DynamicGroupsReusable.SET_IS_PENDING_REVIEW,

    UPDATE_MEMBERSHIP_METHOD: DynamicGroupsReusable.UPDATE_MEMBERSHIP_METHOD,
    UPDATE_MEMBER_SUGGESTIONS_NOTIFY: DynamicGroupsReusable.UPDATE_MEMBER_SUGGESTIONS_NOTIFY,

    ADD_FILTER: DynamicGroupsReusable.ADD_FILTER,
    UPDATE_ATTRIBUTE: DynamicGroupsReusable.UPDATE_ATTRIBUTE,
    UPDATE_OPERATOR: DynamicGroupsReusable.UPDATE_OPERATOR,

    ADD_FILTER_VALUE: DynamicGroupsReusable.ADD_FILTER_VALUE,
    UPDATE_FILTER_VALUE: DynamicGroupsReusable.UPDATE_FILTER_VALUE,
    REMOVE_FILTER_VALUE: DynamicGroupsReusable.REMOVE_FILTER_VALUE,
    RESET_FILTER_VALUES: DynamicGroupsReusable.RESET_FILTER_VALUES,
    RESET_FILTER_VALUES_EXCEPT_FIRST: DynamicGroupsReusable.RESET_FILTER_VALUES_EXCEPT_FIRST,

    ADD_MEMBER_QUERY_EXEMPTION: DynamicGroupsReusable.ADD_MEMBER_QUERY_EXEMPTION,
    REMOVE_MEMBER_QUERY_EXEMPTION: DynamicGroupsReusable.REMOVE_MEMBER_QUERY_EXEMPTION,
    UPDATE_MEMBER_QUERY_EXEMPTIONS: DynamicGroupsReusable.UPDATE_MEMBER_QUERY_EXEMPTIONS,
    UPDATE_EXEMPTION_OPERATION: DynamicGroupsReusable.UPDATE_EXEMPTION_OPERATION,

    INCREMENT_ID: DynamicGroupsReusable.INCREMENT_ID,
    RESET_ID_INCREMENT: DynamicGroupsReusable.RESET_ID_INCREMENT,

    RESET_MEMBER_QUERY_UI: DynamicGroupsReusable.RESET_MEMBER_QUERY_UI,

    RESET_MEMBERSHIP_DATA: DynamicGroupsReusable.RESET_MEMBERSHIP_DATA,

    SET_MEMBER_QUERY: DynamicGroupsReusable.SET_MEMBER_QUERY,

    SET_PREVIOUS_ITEM_MEMBER_QUERY_UI: DynamicGroupsReusable.SET_PREVIOUS_ITEM_MEMBER_QUERY_UI,
    SET_PREVIOUS_ITEM_FIELD: ModelMixin.mutations.SET_PREVIOUS_ITEM_FIELD,
  },
};
